import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, AlertCircle, Github, FileCode, Lightbulb, Send, Paperclip, X, GitPullRequest, MessageSquare, Code, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { CodeEditor } from '../components/CodeEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MaintenanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatImages, setChatImages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [toolStatus, setToolStatus] = useState('');
  const [tokenUsage, setTokenUsage] = useState(null);
  const [contextLimitReached, setContextLimitReached] = useState(false);
  // Changes / PR state
  const [changes, setChanges] = useState([]);
  const [prLoading, setPrLoading] = useState(false);
  const [prResult, setPrResult] = useState(null);
  const [expandedFiles, setExpandedFiles] = useState({});

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchTaskDetails = useCallback(async () => {
    try {
      const [taskRes, logsRes, analysisRes] = await Promise.all([
        fetch(`${API_URL}/api/maintenance/tasks/${id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/maintenance/tasks/${id}/logs`, { credentials: 'include' }),
        fetch(`${API_URL}/api/maintenance/tasks/${id}/analysis`, { credentials: 'include' })
      ]);
      const taskData = await taskRes.json();
      const logsData = await logsRes.json();
      const analysisData = await analysisRes.json();
      setTask(taskData);
      setLogs(logsData.logs || []);
      setAnalysis(analysisData.analysis);
      if (taskData.changes_made?.length) setChanges(taskData.changes_made);
    } catch (error) {
      console.error('Error fetching task details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/tasks/${id}/conversations`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(data.messages || []);
      }
    } catch {}
  }, [id]);

  useEffect(() => {
    fetchTaskDetails();
    fetchConversations();
    const interval = setInterval(() => {
      fetchTaskDetails();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchTaskDetails, fetchConversations]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, streamingContent]);

  const handleImageUpload = async (file) => {
    if (!file?.type?.startsWith('image/') || file.size > 10 * 1024 * 1024) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChatImages(prev => [...prev, { url: data.url, preview: URL.createObjectURL(file) }]);
      }
    } catch (err) { console.error('Upload failed:', err); }
  };

  const handlePasteImage = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (!item.type.startsWith('image/')) continue;
      e.preventDefault();
      await handleImageUpload(item.getAsFile());
    }
  };

  const handleDropImage = async (e) => {
    e.preventDefault(); setDragOver(false);
    for (const file of Array.from(e.dataTransfer.files)) {
      if (file.type.startsWith('image/')) await handleImageUpload(file);
    }
  };

  const sendMessage = async () => {
    if ((!chatInput.trim() && chatImages.length === 0) || isStreaming) return;
    const msg = chatInput.trim();
    const imgs = [...chatImages];
    setChatInput(''); setChatImages([]); setIsStreaming(true);
    setChatMessages(prev => [...prev, { role: 'user', content: msg, images: imgs, created_at: new Date().toISOString() }]);

    try {
      const res = await fetch(`${API_URL}/api/maintenance/tasks/${id}/conversations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, images: imgs }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.type === 'status') {
                setToolStatus(parsed.content);
              } else if (parsed.type === 'token') {
                setToolStatus('');
                fullText += parsed.content;
                setStreamingContent(fullText);
              } else if (parsed.type === 'token_usage') {
                setTokenUsage(parsed.content);
              } else if (parsed.type === 'context_limit') {
                setContextLimitReached(true);
                setChatMessages(prev => [...prev, {
                  role: 'assistant', content: parsed.content,
                  is_context_limit: true,
                  token_usage: parsed.token_usage,
                  created_at: new Date().toISOString()
                }]);
                setStreamingContent(''); setIsStreaming(false);
                setToolStatus('');
              } else if (parsed.type === 'done') {
                setToolStatus('');
                setChatMessages(prev => [...prev, {
                  role: 'assistant', content: parsed.content,
                  tool_calls: parsed.tool_calls, files_changed: parsed.files_changed,
                  debug_rounds: parsed.debug_rounds || 0,
                  token_usage: parsed.token_usage || null,
                  created_at: new Date().toISOString()
                }]);
                if (parsed.token_usage) setTokenUsage(parsed.token_usage);
                setStreamingContent('');
                setIsStreaming(false);
                // Refresh changes
                fetchTaskDetails();
              } else if (parsed.type === 'error') {
                setToolStatus('');
                setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${parsed.content}`, created_at: new Date().toISOString() }]);
                setStreamingContent(''); setIsStreaming(false);
              }
            } catch {}
          }
        }
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Connection error: ${err.message}`, created_at: new Date().toISOString() }]);
      setStreamingContent(''); setIsStreaming(false);
    }
  };

  const createPR = async () => {
    setPrLoading(true); setPrResult(null);
    try {
      const res = await fetch(`${API_URL}/api/maintenance/tasks/${id}/create-pr`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `[CodeFoundry] ${task?.task_type?.replace('_', ' ')} - ${task?.task_description?.slice(0, 80)}`,
          description: `## CodeFoundry Maintenance\n\n**Task:** ${task?.task_description}\n\n### Files Changed\n${changes.map(c => `- \`${c.path}\``).join('\n')}`,
        }),
      });
      const data = await res.json();
      setPrResult(data);
      if (data.pr_url) fetchTaskDetails();
    } catch (err) {
      setPrResult({ error: err.message });
    } finally { setPrLoading(false); }
  };

  const toggleFile = (path) => setExpandedFiles(prev => ({ ...prev, [path]: !prev[path] }));

  const statusColor = (s) => ({ created: '#6b7280', running: '#f59e0b', analyzing: '#f59e0b', completed: '#22c55e', failed: '#ef4444', pr_created: '#8b5cf6' }[s] || '#6b7280');
  const logColor = (l) => ({ info: '#9ca3af', success: '#22c55e', warning: '#f59e0b', error: '#ef4444' }[l] || '#9ca3af');

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: '4rem' }}><div className="spinner" style={{ margin: '0 auto' }} /><p style={{ marginTop: '1rem', color: 'hsl(var(--muted-foreground))' }}>Loading...</p></div>;
  if (!task) return <div className="container" style={{ textAlign: 'center', padding: '4rem' }}><AlertCircle size={48} style={{ color: 'hsl(var(--destructive))', margin: '0 auto 1rem' }} /><p>Task not found</p></div>;

  const isAnalysisComplete = task.status === 'completed' || task.status === 'pr_created';

  return (
    <div className="container" data-testid="maintenance-detail-page" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={() => navigate('/')} className="btn btn-secondary" style={{ marginBottom: '1rem' }} data-testid="back-button">
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <Github size={20} />
              <h1 style={{ fontSize: '1.25rem', margin: 0 }}>Maintenance Task</h1>
            </div>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', margin: '0.25rem 0' }}>{task.repo_url}</p>
            <p style={{ fontSize: '0.9rem', margin: 0 }}>{task.task_description}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>{task.task_type?.replace('_', ' ').toUpperCase()}</span>
            <span style={{ padding: '0.35rem 0.75rem', borderRadius: 'var(--radius)', background: `${statusColor(task.status)}20`, color: statusColor(task.status), fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              {task.status === 'running' || task.status === 'analyzing' ? <Clock size={14} /> : task.status === 'completed' || task.status === 'pr_created' ? <CheckCircle size={14} /> : task.status === 'failed' ? <AlertCircle size={14} /> : null}
              {task.status === 'pr_created' ? 'PR Created' : task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '1px solid hsl(var(--border))', paddingBottom: '0' }}>
        {[
          { key: 'chat', label: 'Chat', icon: <MessageSquare size={14} />, show: isAnalysisComplete },
          { key: 'editor', label: 'Editor', icon: <Pencil size={14} />, show: isAnalysisComplete },
          { key: 'analysis', label: 'Analysis', icon: <Lightbulb size={14} />, show: true },
          { key: 'changes', label: `Changes${changes.length ? ` (${changes.length})` : ''}`, icon: <Code size={14} />, show: true },
          { key: 'logs', label: 'Logs', icon: <Clock size={14} />, show: true },
        ].filter(t => t.show).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
            style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #6366f1' : '2px solid transparent', color: activeTab === tab.key ? '#6366f1' : 'hsl(var(--muted-foreground))', cursor: 'pointer', fontWeight: activeTab === tab.key ? 600 : 400, transition: 'all 0.15s' }}
          >{tab.icon} {tab.label}</button>
        ))}
      </div>

      {/* Chat Tab */}
      {activeTab === 'chat' && isAnalysisComplete && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)' }}>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem', padding: '0.5rem' }}>
            {chatMessages.length === 0 && !streamingContent && (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'hsl(var(--muted-foreground))' }}>
                <MessageSquare size={40} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Start chatting about your codebase</p>
                <p style={{ fontSize: '0.8rem' }}>Paste screenshots (Ctrl+V), drag images, or describe what you need.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', maxWidth: '500px', margin: '1.5rem auto 0' }}>
                  {[
                    `Fix the ${task.task_type?.replace('_', ' ')} issue described`,
                    'Show me the relevant code that needs to change',
                    'Generate the code changes and create a PR',
                    'What are the potential risks of this change?'
                  ].map((prompt, i) => (
                    <button key={i} onClick={() => { setChatInput(prompt); }} data-testid={`prompt-${i}`}
                      style={{ padding: '0.6rem', fontSize: '0.75rem', background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'hsl(var(--foreground))', textAlign: 'left', lineHeight: 1.4 }}
                    >{prompt}</button>
                  ))}
                </div>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '85%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: msg.role === 'user' ? '#6366f1' : 'hsl(var(--muted))', color: msg.role === 'user' ? '#fff' : 'hsl(var(--foreground))', fontSize: '0.875rem', lineHeight: 1.6 }}>
                  {msg.images?.map((img, j) => <img key={j} src={img.preview || img.url} alt="" style={{ maxWidth: '300px', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'block' }} />)}
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown components={{
                      code({ inline, className, children }) {
                        return inline ? <code style={{ background: 'hsl(var(--background))', padding: '0.15rem 0.35rem', borderRadius: '3px', fontSize: '0.8rem' }}>{children}</code> :
                          <pre style={{ background: 'hsl(222 22% 8%)', padding: '0.75rem', borderRadius: '0.5rem', overflow: 'auto', fontSize: '0.8rem', margin: '0.5rem 0' }}><code>{children}</code></pre>;
                      },
                      p({ children }) {
                        return <div style={{ marginBottom: '0.5rem' }}>{children}</div>;
                      }
                    }}>{msg.content}</ReactMarkdown>
                  ) : msg.content}
                </div>
                <span style={{ fontSize: '0.65rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem', padding: '0 0.25rem', display: 'flex', gap: '0.5rem' }}>
                  {msg.role === 'user' ? 'You' : 'CodeFoundry AI'}
                  {msg.tool_calls > 0 && <span style={{ opacity: 0.6 }}>{msg.tool_calls} tool calls</span>}
                  {msg.files_changed > 0 && <span style={{ color: '#22c55e', opacity: 0.8 }}>{msg.files_changed} files changed</span>}
                  {msg.debug_rounds > 0 && <span style={{ color: '#f59e0b', opacity: 0.8 }}>{msg.debug_rounds} debug rounds</span>}
                </span>
              </div>
            ))}
            {(toolStatus || streamingContent) && (
              <div style={{ marginBottom: '1rem' }}>
                {toolStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', padding: '0.5rem 0.75rem', background: 'hsl(var(--muted))', borderRadius: '0.5rem', fontSize: '0.8rem', color: '#6366f1', maxWidth: '85%' }}>
                    <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    {toolStatus}
                  </div>
                )}
                {streamingContent && (
                  <div style={{ maxWidth: '85%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'hsl(var(--muted))', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    <ReactMarkdown components={{
                      code({ inline, children }) {
                        return inline ? <code style={{ background: 'hsl(var(--background))', padding: '0.15rem 0.35rem', borderRadius: '3px', fontSize: '0.8rem' }}>{children}</code> :
                          <pre style={{ background: 'hsl(222 22% 8%)', padding: '0.75rem', borderRadius: '0.5rem', overflow: 'auto', fontSize: '0.8rem', margin: '0.5rem 0' }}><code>{children}</code></pre>;
                      },
                      p({ children }) {
                        return <div style={{ marginBottom: '0.5rem' }}>{children}</div>;
                      }
                    }}>{streamingContent}</ReactMarkdown>
                    <span className="streaming-cursor" />
                  </div>
                )}
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Context Limit Banner + Continue */}
          {contextLimitReached && (
            <div data-testid="context-limit-banner" style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '0.75rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.125rem' }}>Context Window Reached</p>
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Session saved. Continue in a new session to keep all progress.</p>
              </div>
              <button
                data-testid="continue-session-btn"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/sessions/continue`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ session_id: id }),
                    });
                    const data = await res.json();
                    if (data.new_task_id) {
                      navigate(`/maintenance/${data.new_task_id}`);
                    }
                  } catch (err) {
                    console.error('Continue failed:', err);
                  }
                }}
                style={{ padding: '0.5rem 1.25rem', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
              >
                Continue Session
              </button>
            </div>
          )}

          {/* Token Usage Indicator */}
          {tokenUsage && (
            <div data-testid="token-usage-bar" style={{ marginBottom: '0.5rem', padding: '0.375rem 0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
              <span>Context: {tokenUsage.percent_used}%</span>
              <div style={{ flex: 1, height: '4px', background: 'hsl(var(--border))', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(tokenUsage.percent_used, 100)}%`, height: '100%', background: tokenUsage.percent_used > 75 ? '#f59e0b' : '#22c55e', borderRadius: '2px', transition: 'width 0.3s' }} />
              </div>
              <span>{tokenUsage.api_calls} calls</span>
            </div>
          )}

          {/* Image previews */}
          {chatImages.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              {chatImages.map((img, i) => (
                <div key={i} style={{ position: 'relative', width: '60px', height: '60px' }}>
                  <img src={img.preview || img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.5rem', border: '1px solid hsl(var(--border))' }} />
                  <button onClick={() => setChatImages(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', padding: 0 }}><X size={10} /></button>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => Array.from(e.target.files).forEach(handleImageUpload)} />
            <button onClick={() => fileInputRef.current?.click()} style={{ padding: '0.5rem', background: 'none', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }} data-testid="attach-button"><Paperclip size={18} /></button>
            <textarea
              placeholder="Ask about your code, request changes... (Ctrl+V to paste screenshots)"
              value={chatInput}
              onChange={(e) => { setChatInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              onPaste={handlePasteImage}
              onDrop={handleDropImage}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              rows={1}
              data-testid="maintenance-chat-input"
              style={{ flex: 1, resize: 'none', minHeight: '40px', maxHeight: '150px', fontSize: '0.875rem', padding: '0.5rem 0.75rem', border: `1px solid ${dragOver ? '#6366f1' : 'hsl(var(--border))'}`, borderRadius: '0.5rem', background: dragOver ? 'hsl(var(--accent) / 0.05)' : 'hsl(var(--background))', color: 'hsl(var(--foreground))', lineHeight: 1.5, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.15s' }}
              onFocus={e => e.target.style.borderColor = '#6366f1'}
              onBlur={e => { e.target.style.borderColor = 'hsl(var(--border))'; setDragOver(false); }}
            />
            <button onClick={sendMessage} disabled={isStreaming || (!chatInput.trim() && chatImages.length === 0)} data-testid="send-button"
              style={{ padding: '0.5rem 1rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: isStreaming ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
              <Send size={16} /> Send
            </button>
          </div>
        </div>
      )}

      {/* Editor Tab */}
      {activeTab === 'editor' && isAnalysisComplete && (
        <div style={{ height: 'calc(100vh - 280px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            padding: '0.5rem 0.75rem', background: '#161b22',
            borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '1px solid #30363d',
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            fontSize: '0.75rem', color: '#8b949e', fontFamily: 'IBM Plex Mono',
          }}>
            <FileCode size={14} style={{ color: '#58a6ff' }} />
            <span>Scratch editor with AI completions</span>
            <span style={{ opacity: 0.5 }}>Tab to accept suggestions</span>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <CodeEditor
              defaultValue={`# Working on: ${task?.repo_url || 'repository'}\n# Task: ${task?.task_description || ''}\n# Use this editor to draft code with AI-powered completions\n\n`}
              filename="scratch.py"
              language="python"
              enableCompletion={true}
            />
          </div>
        </div>
      )}

      {/* Analysis Tab */}
      {activeTab === 'analysis' && (
        <div>
          {!analysis ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'hsl(var(--muted-foreground))' }}>
              {task.status === 'running' || task.status === 'analyzing' ? (
                <><div className="spinner" style={{ margin: '0 auto 1rem' }} /><p>Analysis in progress...</p></>
              ) : <p>No analysis available yet.</p>}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {[
                  { label: 'Complexity', value: analysis.complexity || 'Unknown' },
                  { label: 'Est. Files to Change', value: analysis.estimated_changes || 0 },
                  { label: 'Recommendations', value: analysis.recommendations?.length || 0 },
                ].map((m, i) => (
                  <div key={i} className="card" style={{ background: 'hsl(var(--muted))' }}>
                    <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.35rem' }}>{m.label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600, textTransform: 'capitalize' }}>{m.value}</div>
                  </div>
                ))}
              </div>
              {analysis.summary && (
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Summary</h3>
                  <div style={{ background: 'hsl(var(--muted))', padding: '0.75rem', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap', fontFamily: 'IBM Plex Mono', fontSize: '0.8rem', lineHeight: 1.6 }}>{analysis.summary}</div>
                </div>
              )}
              {analysis.recommendations?.length > 0 && (
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>Recommendations</h3>
                  <ol style={{ paddingLeft: '1.25rem', fontSize: '0.85rem', lineHeight: 1.8 }}>
                    {analysis.recommendations.map((r, i) => <li key={i} style={{ marginBottom: '0.35rem' }}>{r}</li>)}
                  </ol>
                </div>
              )}
              {analysis.relevant_files?.length > 0 && (
                <div className="card">
                  <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}><FileCode size={14} /> Relevant Files</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                    {analysis.relevant_files.slice(0, 12).map((f, i) => (
                      <span key={i} style={{ padding: '0.2rem 0.6rem', background: 'hsl(var(--secondary))', borderRadius: '9999px', fontSize: '0.7rem', fontFamily: 'IBM Plex Mono' }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Changes Tab */}
      {activeTab === 'changes' && (
        <div>
          {changes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'hsl(var(--muted-foreground))' }}>
              <Code size={40} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>No code changes yet</p>
              <p style={{ fontSize: '0.8rem' }}>Use the Chat tab to discuss and generate code modifications.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>{changes.length} file(s) modified</span>
                <button onClick={createPR} disabled={prLoading || !changes.length} data-testid="create-pr-button"
                  style={{ padding: '0.5rem 1rem', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', fontWeight: 500, opacity: prLoading ? 0.6 : 1 }}>
                  <GitPullRequest size={16} /> {prLoading ? 'Creating PR...' : 'Create Pull Request'}
                </button>
              </div>

              {prResult && (
                <div className="card" style={{ marginBottom: '1rem', background: prResult.pr_url ? '#22c55e10' : '#ef444410', border: `1px solid ${prResult.pr_url ? '#22c55e40' : '#ef444440'}` }}>
                  {prResult.pr_url ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle size={18} style={{ color: '#22c55e' }} />
                      <span style={{ fontSize: '0.85rem' }}>PR created: </span>
                      <a href={prResult.pr_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: '0.85rem' }}>{prResult.pr_url}</a>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <AlertCircle size={18} style={{ color: '#ef4444' }} />
                      <span style={{ fontSize: '0.85rem', color: '#ef4444' }}>{prResult.error || prResult.message}</span>
                    </div>
                  )}
                </div>
              )}

              {task.pr_url && !prResult && (
                <div className="card" style={{ marginBottom: '1rem', background: '#8b5cf610', border: '1px solid #8b5cf640' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <GitPullRequest size={18} style={{ color: '#8b5cf6' }} />
                    <span style={{ fontSize: '0.85rem' }}>PR: </span>
                    <a href={task.pr_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: '0.85rem' }}>{task.pr_url}</a>
                  </div>
                </div>
              )}

              {changes.map((change, i) => (
                <div key={i} className="card" style={{ marginBottom: '0.5rem', padding: '0' }}>
                  <button onClick={() => toggleFile(change.path)} style={{ width: '100%', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--foreground))', fontSize: '0.85rem', textAlign: 'left' }} data-testid={`file-${change.path}`}>
                    {expandedFiles[change.path] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <FileCode size={14} style={{ color: '#6366f1' }} />
                    <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '0.8rem' }}>{change.path}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#22c55e' }}>{change.status || 'pending'}</span>
                  </button>
                  {expandedFiles[change.path] && (
                    <pre style={{ margin: 0, padding: '0.75rem 1rem', background: 'hsl(222 22% 8%)', fontSize: '0.75rem', overflow: 'auto', maxHeight: '400px', borderTop: '1px solid hsl(var(--border))' }}><code style={{ color: '#e2e8f0' }}>{change.content}</code></pre>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="card" style={{ padding: '0' }}>
          <div style={{ background: 'hsl(222 22% 8%)', borderRadius: 'var(--radius)', padding: '1rem', maxHeight: '500px', overflowY: 'auto', fontFamily: 'IBM Plex Mono', fontSize: '0.8rem' }}>
            {logs.length === 0 ? (
              <div style={{ color: '#9ca3af', opacity: 0.5 }}>No logs yet...</div>
            ) : logs.map((log, i) => (
              <div key={i} style={{ color: logColor(log.level), marginBottom: '0.35rem', display: 'flex', gap: '0.75rem' }}>
                <span style={{ opacity: 0.5, minWidth: '80px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                <span>{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceDetail;
